import { normalizePath } from "../../../apps/appFiles"
import { IAppFiles, IFolderContent } from "../../../apps/types"
import { handleApiError } from "../../../error"
import { omniaResolve } from "../../../promises"
import { IOmniaShell } from "../../../shell"
import { defineDirective } from "../../../utils/angular"
import { escape } from "../../../utils/regexp"
import { endsWith } from "../../../utils/utils"
import { IFileSelectionContext } from "../types"
import { createStorageObjectVm, StorageObjectVM } from "./storageObjectVm"

type FolderContent = IFolderContent

interface Folder extends FolderContent {
    name: string
    shouldRefresh: boolean
}

/** @internal */
export class SelectionCtrl {

    static create(): ng.IDirective {
        return defineDirective(SelectionCtrl, "shared/components/files/selection/selection.html", {
            "session": "<",
            "context": "<",
            "export": "<",
            "showHeader": "<",
            "prompt": "<",
            "checkboxes": "<"
        })
    }

    static $inject = ["OmniaShell"]

    constructor(private readonly shell: IOmniaShell) {
    }

    private init = false

    ready: boolean
    working: boolean

    showHeader: boolean
    prompt: string
    export: boolean
    checkboxes: boolean

    private _session: IAppFiles
    context: IFileSelectionContext

    files: StorageObjectVM[]
    filteredFiles: StorageObjectVM[]

    location: Folder[] = []

    private _search: string

    $onInit() {
        this.setup()
    }

    get session() {
        return this._session
    }

    set session(value) {
        this._session = value
        this.setup()
    }

    setup() {

        this.ready = false

        const { _session, context } = this

        if (_session && context) {

            context.canSelectAll = s => this.canSelectAll(s)
            context.selectAll = s => this.selectAll(s)
            context.refresh = () => this.refresh()
            context.canRefresh = () => this.ready && (!this.working)
            context.isLoading = () => (!this.ready) || this.working

            const init = context?.initialSelection

            const p = init && (!this.init)
                ? this.restoreInitialSelection(init)
                : this.openRootFolder()

            p.finally(() => this.ready = true)

        }

    }

    private refresh() {

        const { currentFolder, _session } = this

        _session.refresh()

        this.location = this.location?.map(l => ({ ...l, shouldRefresh: true }))

        if (!currentFolder) {
            this.openRootFolder()
        }
        else {

            this.setCurrentFolder(currentFolder.name, currentFolder.path, "refresh").then(ok => {

                if (!ok) {
                    this.openRootFolder(true)
                }

            })

        }

    }

    get search(): string {
        return this._search
    }

    set search(value: string) {
        this._search = value
        this.applyFilter()
    }

    get canSelectFolders(): boolean {

        const { context, export: exp, session } = this

        if (context?.canSelectFolders === false) {
            return false
        }

        if (exp) {
            return true
        }

        return session?.canSelectFolders && context.multipleSelection

    }

    get showFiles() {
        return !this.export
    }

    get selectedFiles(): StorageObjectVM[] {
        return this.files ? this.files.filter(f => f.selected) : []
    }

    get topLevel() {
        return this.location.length <= 1
    }

    get currentFolder() {
        const { location } = this
        return location[location.length - 1]
    }

    canSelect(): boolean {
        if (!this.ready) return false
        if (this.working) return false
        if (!this.checkboxes) return true
        return this.context.multipleSelection || (!this.selectedFiles.length)
    }

    canSelectItem(f: StorageObjectVM) {
        return (!f.isFolder) || this.canSelectFolders
    }

    isVisible(f: StorageObjectVM) {
        return f.isFolder || this.showFiles
    }


    selectOrOpen(file: StorageObjectVM) {

        if (this.working) {
            return
        }

        if (this.checkboxes && file.object.IsFolder) {
            this.setCurrentFolder(file.name, file.object.Path)
            return
        }

        if (file.selected || this.canSelect()) {
            file.selected = !file.selected

            if (file.selected && (!this.context.multipleSelection)) {
                this.files.forEach(f => f.selected = f == file)
            }

            this.selectionChange()
        }

    }

    moveUp(to: Folder) {

        if (this.working) {
            return
        }

        const index = to ? this.location.indexOf(to) : -1

        if (index >= 0) {
            this.location.splice(index + 1)
        }
        else {
            this.location.pop()
        }

        const { currentFolder } = this

        if (this.currentFolder.shouldRefresh) {
            this.setCurrentFolder(currentFolder.name, currentFolder.path, "refresh")
        }
        else {
            this.updateOnCurrentFolderChanged()
        }

    }

    openFolder(f: StorageObjectVM) {
        this.setCurrentFolder(f.name, f.object.Path)
    }

    private selectionChange() {
        this.context.files = this.selectedFiles.map(f => {
            return {
                fileName: f.name,
                isFolder: f.isFolder,
                path: f.object.Path,
                connectionId: this.session.connectionId
            }
        })
    }

    private openRootFolder(reset = false) {

        this.onBrowse()

        if (!reset) {

            const state = this._session.getConnectionState()

            if (state) {
                this.location = state.location?.map(l => ({ ...l, shouldRefresh: false })) ?? []
                this.updateOnCurrentFolderChanged()
                return omniaResolve()
            }

        }

        this.working = true

        return this.session
            .getFolderContent("/").then(r => {

                this.working = false

                this.context.currentPath = r.path

                this.location = [
                    {
                        name: this._session.name,
                        shouldRefresh: false,
                        ...r
                    }
                ]

                const defaultOrg = this.session.getDefaultOrganisation()

                if (defaultOrg) {
                    const defaultPath = "/org:" + defaultOrg.id
                    const dir = this.files.find(f => endsWith(f.object.Path, defaultPath))
                    if (dir) {
                        return this.setCurrentFolder(dir.name, dir.object.Path)
                    }
                }

                this.updateOnCurrentFolderChanged()

            })
            .finally(() => this.working = false)


    }

    /**
     * Restores an initial selection of a single folder
     *
     * This is only used when selecting a target folder to export to,
     * and will not work for files, only folders
     *
     * @param path Path to select
     */
    private restoreInitialSelection(path: string) {

        let p: ng.IPromise<any>
        let name: string = undefined

        this.init = true

        if (!(this.export && path)) {
            return this.openRootFolder()
        }

        const i = path.lastIndexOf("/")

        if (i < 0) {
            return this.openRootFolder()
        }

        if (i == 0) {
            p = this.openRootFolder()
            name = path.substring(1)
        }
        else {
            name = path.substring(i + 1)
            path = path.substring(0, i)
            p = this.setCurrentFolder(getNameFromPath(path), path, "init")
        }

        let restored = false

        p = p.then(() => {

            const match = this.files.find(f => f.isFolder && (f.name == name))

            if (match) {
                match.selected = true
                restored = true
                this.selectionChange()
            }

        })
            .finally(() => {

                if (!restored) {
                    this.openRootFolder(true)
                }

            })

        return p

    }

    private setCurrentFolder(name: string, path: string, reason: "open" | "refresh" | "init" = "open") {

        const refreshing = reason === "refresh"

        this.onBrowse()
        this.working = true

        return this.session
            .getFolderContent(normalizePath(path))
            .then(r => {

                this.context.currentPath = path

                if (refreshing) {
                    this.location.pop()
                }

                this.location = this.location.concat({
                    name,
                    shouldRefresh: false,
                    ...r
                })

                this.updateOnCurrentFolderChanged()

                return true

            })
            .catch(r => {

                if (reason !== "open") {
                    return false
                }

                return handleApiError(this.shell, r).then(() => false)

            })
            .finally(() => this.working = false)

    }

    private updateOnCurrentFolderChanged() {

        const { currentFolder, _session } = this

        const content = currentFolder?.content ?? []

        this.files = content.map(createStorageObjectVm)
        this.selectionChange()

        this.applyFilter()

        _session.setConnectionState({ location: this.location })

    }

    private applyFilter() {

        const { search, files } = this

        this.filteredFiles = files
        if (search && files) {
            const re = new RegExp(escape(search), "i")
            this.filteredFiles = this.files.filter(f => f.selected || re.test(f.name))
        }

    }

    private onBrowse() {
        this._search = null
        this.files = []
        this.selectionChange()
    }

    canSelectAll(select: boolean) {

        const files = this.filteredFiles ?? this.files

        return this.ready
            && (!this.working)
            && this.context?.multipleSelection
            && files
            && files.some(f => (!f.isFolder) && (f.selected != select))

    }

    selectAll(select: boolean) {
        if (this.canSelectAll(select)) {
            const files = this.filteredFiles ?? this.files
            files.forEach(f => f.selected = select && (!f.isFolder))
            this.selectionChange()
        }
    }
}

function getNameFromPath(path: string) {
    const p = path.split("/")
    return p[p.length - 1]
}
